import { Link } from "react-router-dom";
import LegalLayout from "@/components/LegalLayout";
import ConsentFigure from "@/components/ConsentFigure";

const UL = ({ items }) => (
  <ul className="list-disc pl-5 space-y-2 marker:text-[#8b7cff]/70">
    {items.map((it, i) => <li key={i}>{it}</li>)}
  </ul>
);

const sections = [
  {
    id: "scope",
    title: "Who we are & scope",
    body: (
      <>
        <p>
          Agentica AI Solutions ("Agentica," "we," "us," or "our") is an AI software development company
          headquartered in Fort Lauderdale, Florida, United States. We provide consulting and engineering
          services in Generative AI and Agentic AI, and we build and sell AI-powered software products,
          including conversational agents that may communicate with end users by text message (SMS/MMS),
          email, voice, or chat.
        </p>
        <p>
          This Privacy Policy explains how we collect, use, disclose, and protect personal information when
          you visit agentica-sol.com (the "Site"), engage our services, or interact with our products
          (collectively, the "Services"). It also describes the choices available to you.
        </p>
        <p>
          Where we process personal information on behalf of a business customer that has deployed one of
          our products (for example, a company using an Agentica agent to message its own customers), that
          customer is the data controller and we act as a service provider/processor under our agreement with
          them. Their privacy policy governs that processing; this Policy applies to the information we
          control.
        </p>
      </>
    ),
  },
  {
    id: "collect",
    title: "Information we collect",
    body: (
      <>
        <p><strong className="text-white/85">Information you provide to us</strong></p>
        <UL items={[
          "Contact details — name, email address, phone number, company, job title, and the content of your message when you use our contact form, book a call, email us, or speak with our website assistant.",
          "Account and billing information — when you purchase or license a product, we collect business details and payment information (processed by our payment providers; we do not store full card numbers).",
          "Communications — records of your correspondence with us, including support requests, feedback, and chat transcripts with our AI assistant.",
          "Consent records — the date, time, method, and wording of any consent you give us, including consent to receive text messages.",
        ]} />
        <p className="pt-2"><strong className="text-white/85">Information collected automatically</strong></p>
        <UL items={[
          "Device and usage data — IP address, browser type, operating system, referring URLs, pages viewed, and interactions with the Site.",
          "Cookies and similar technologies — used to keep the Site working, remember preferences, and understand usage. See the Cookies section below.",
        ]} />
        <p className="pt-2"><strong className="text-white/85">Information from third parties</strong></p>
        <UL items={[
          "Business partners, public sources, and professional networks (e.g., LinkedIn) where relevant to a business relationship.",
          "Our business customers, when they configure one of our products to communicate with their end users.",
        ]} />
      </>
    ),
  },
  {
    id: "use",
    title: "How we use information",
    body: (
      <>
        <p>We use personal information to:</p>
        <UL items={[
          "Provide, operate, maintain, and improve the Site, our Services, and our products.",
          "Respond to inquiries, schedule discovery calls, and deliver customer support.",
          "Send transactional and service communications, and — only with your consent — marketing or conversational messages by text, email, or phone.",
          "Build, test, monitor, and improve our AI systems, including quality assurance of agent conversations. We do not use your personal information to train publicly available foundation models.",
          "Detect, prevent, and address fraud, abuse, security incidents, and technical issues.",
          "Comply with legal obligations, enforce our Terms, and protect our rights and the rights of others.",
        ]} />
      </>
    ),
  },
  {
    id: "sms",
    title: "Text messaging (SMS/MMS) & consent",
    body: (
      <>
        <p>
          Some of our products send text messages to end users' mobile phone numbers. We take messaging
          consent seriously and we will <strong className="text-white/85">always obtain your express consent
          before initiating a text conversation</strong>.
        </p>
        <p><strong className="text-white/85">How you opt in.</strong> You may consent to receive text messages by (a) checking a distinct, stand-alone SMS consent checkbox on one of our own or our customers' web forms — this box is always unchecked by default, is never pre-selected, and is kept separate from terms acceptance or any other consent, (b) texting a keyword to one of our numbers, (<span>c</span>) verbally agreeing during a call, or (d) another affirmative action that clearly indicates agreement. Consent is never a condition of purchase. We record the date, time, source, and wording of your consent.</p>
        <p><strong className="text-white/85">What you'll receive.</strong> Depending on the program you enrolled in, messages may include conversational replies from an AI agent, appointment or order updates, account notifications, customer-care responses, and — where you separately consented — promotional offers. Message frequency varies based on your interaction.</p>
        <ConsentFigure />
        <p><strong className="text-white/85">Costs.</strong> Message and data rates may apply according to your mobile carrier plan. Carriers are not liable for delayed or undelivered messages.</p>
        <p><strong className="text-white/85">How to opt out.</strong> You can cancel at any time by replying <span className="font-display text-white tracking-wide">STOP</span> to any message. After you send STOP, we will send one final message confirming you have been unsubscribed, and you will receive no further messages from that program. To rejoin, text the program keyword or opt in again through the original method.</p>
        <p><strong className="text-white/85">Help.</strong> Reply <span className="font-display text-white tracking-wide">HELP</span> to any message for assistance, or contact us at ikumar@agentica-sol.com or +1 754 218 2448.</p>
        <p><strong className="text-white/85">No sharing for marketing.</strong> Mobile phone numbers and text-messaging opt-in data are never sold, rented, or shared with third parties or affiliates for their own marketing or promotional purposes. We share this information only with the messaging service providers and telecom carriers needed to deliver your messages, and only for that purpose.</p>
        <p><strong className="text-white/85">AI disclosure.</strong> When you text with one of our agents you are interacting with an automated AI system. You can request a human at any time by replying with a request such as "agent" or "human," or by using the contact details above.</p>
      </>
    ),
  },
  {
    id: "share",
    title: "How we share information",
    body: (
      <>
        <p>We do not sell personal information. We share it only as described here:</p>
        <UL items={[
          "Service providers — hosting, cloud infrastructure, email delivery, SMS/telecom carriers, analytics, payment processing, and AI model providers that process data under contractual confidentiality and security obligations.",
          "Business customers — when we operate a product on a customer's behalf, we return the resulting data (including conversation transcripts) to that customer.",
          "Legal and safety — to comply with law, regulation, legal process, or governmental request; or to protect the rights, property, or safety of Agentica, our users, or the public.",
          "Business transfers — in connection with a merger, acquisition, financing, or sale of assets, subject to the commitments of this Policy.",
          "With your direction — when you ask us to share information or otherwise consent.",
        ]} />
      </>
    ),
  },
  {
    id: "ai",
    title: "AI systems & automated processing",
    body: (
      <>
        <p>
          Our Services use large language models and other machine-learning systems. Inputs you provide to
          our AI assistants (including text messages and chat) may be processed by third-party model
          providers under agreements that prohibit them from using your data to train their models.
        </p>
        <p>
          Our agents are designed to assist and inform. They do not make legally or similarly significant
          decisions about you without human involvement. If you believe an automated interaction produced an
          inaccurate or harmful result, please contact us and we will review it.
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies & analytics",
    body: (
      <>
        <p>
          We use strictly necessary cookies to run the Site and may use analytics cookies to understand how
          visitors use it. You can control cookies through your browser settings; disabling some cookies may
          affect Site functionality. We honor Global Privacy Control signals where required by law.
        </p>
      </>
    ),
  },
  {
    id: "retention",
    title: "Data retention & security",
    body: (
      <>
        <p>
          We keep personal information only as long as necessary for the purposes described in this Policy,
          to comply with legal obligations (for example, maintaining proof of messaging consent and opt-out
          requests), resolve disputes, and enforce agreements. Consent and opt-out records are retained for
          at least as long as required by applicable telecom regulations.
        </p>
        <p>
          We use administrative, technical, and physical safeguards — including encryption in transit,
          access controls, and least-privilege practices — designed to protect your information. No system is
          perfectly secure, and we cannot guarantee absolute security.
        </p>
      </>
    ),
  },
  {
    id: "rights",
    title: "Your rights & choices",
    body: (
      <>
        <p>Depending on where you live, you may have the right to:</p>
        <UL items={[
          "Access, correct, or delete the personal information we hold about you.",
          "Receive a portable copy of your information.",
          "Opt out of marketing emails (use the unsubscribe link) and text messages (reply STOP).",
          "Object to or restrict certain processing, and withdraw consent where processing is based on consent.",
          "Not be discriminated against for exercising your rights.",
        ]} />
        <p>
          To exercise any right, email <a href="mailto:ikumar@agentica-sol.com" className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white">ikumar@agentica-sol.com</a>.
          We will verify your request and respond within the time required by applicable law. You may
          designate an authorized agent to make a request on your behalf.
        </p>
        <p>
          <strong className="text-white/85">California residents.</strong> We do not sell or share personal
          information for cross-context behavioral advertising. You may request the categories and specific
          pieces of information we have collected about you and the categories of third parties with whom it
          was shared.
        </p>
        <p>
          <strong className="text-white/85">EEA/UK residents.</strong> Our legal bases are performance of a
          contract, legitimate interests, consent, and compliance with legal obligations. You may lodge a
          complaint with your local supervisory authority.
        </p>
      </>
    ),
  },
  {
    id: "children",
    title: "Children & international transfers",
    body: (
      <>
        <p>
          Our Services are intended for businesses and are not directed to children under 13 (or under 16
          where applicable). We do not knowingly collect personal information from children. If you believe a
          child has provided us with information, contact us and we will delete it.
        </p>
        <p>
          We are based in the United States and may process information in the U.S. and other countries.
          Where required, we use appropriate safeguards, such as standard contractual clauses, for
          international transfers.
        </p>
      </>
    ),
  },
  {
    id: "changes",
    title: "Changes & contact",
    body: (
      <>
        <p>
          We may update this Policy from time to time. The "Last updated" date at the top indicates the most
          recent revision. Material changes will be posted on this page and, where appropriate, communicated
          to you directly.
        </p>
        <p>
          Agentica AI Solutions · Fort Lauderdale, Florida, United States ·{" "}
          <a href="mailto:ikumar@agentica-sol.com" className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white">ikumar@agentica-sol.com</a> · +1 754 218 2448
        </p>
        <p>
          Please also review our <Link to="/terms" className="text-white underline underline-offset-4 decoration-white/30 hover:decoration-white" data-testid="privacy-to-terms-link">Terms &amp; Conditions</Link>.
        </p>
      </>
    ),
  },
];

export default function PrivacyPolicy() {
  return (
    <LegalLayout
      testId="privacy-page"
      overline="Legal — Privacy Policy"
      title="Your data, handled with intent."
      intro="How Agentica AI Solutions collects, uses, and protects personal information across our website, consulting engagements, and AI products — including the text-messaging consent practices that govern our conversational agents."
      updated="July 2026"
      sections={sections}
    />
  );
}
